import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { KeyRound, Crown, Gem, ArrowRight, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

const TIERS = [
  {
    step: "STEP 01",
    tag: "卡密激活",
    name: "普通会员",
    desc: "淘宝购买卡密激活会员资格，进入项目交流群，每日分享日赚500+稳定项目，邀请1位成员再得 ¥128 现金奖励。",
    icon: KeyRound,
  },
  {
    step: "STEP 02",
    tag: "邀请 ×10",
    name: "VIP项目交流群",
    desc: "累计邀请满 10 位成员，解锁 VIP 项目交流群：群二维码、群ID与直达链接全面开放。",
    icon: Crown,
  },
  {
    step: "STEP 03",
    tag: "邀请 ×30",
    name: "超级VIP项目交流群",
    desc: "累计邀请满 30 位成员，解锁超级 VIP 项目交流群，对接顶级一手项目资源与核心圈层。",
    icon: Gem,
  },
];

export default function JoinSection() {
  const { user, openAuth } = useAuth();
  const [taobao, setTaobao] = useState("https://www.taobao.com");

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      if (r.data?.taobao_url) setTaobao(r.data.taobao_url);
    }).catch(() => {});
  }, []);

  return (
    <section id="join" className="relative py-24 lg:py-36 px-5 lg:px-8 grain" data-testid="join-section">
      <div className="mx-auto max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-16"
        >
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#D4AF37] mb-4">MEMBERSHIP · 会员体系</p>
          <h2 className="font-serif-sc text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            一张卡密，开启<span className="text-gold-grad">合赢之路</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 }}
              className="panel relative p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-mono-num text-[10px] tracking-[0.3em] text-gray-600">{t.step}</span>
                <span className="text-[10px] tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/30 px-2.5 py-1">{t.tag}</span>
              </div>
              <t.icon className="w-8 h-8 text-[#D4AF37] mb-6" />
              <h3 className="font-serif-sc text-xl font-bold tracking-wide mb-3">{t.name}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{t.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative max-w-2xl mx-auto panel p-8 lg:p-10 gold-glow"
        >
          <div className="h-px absolute top-0 left-0 right-0 shimmer-line" />
          {user ? (
            user.is_activated ? (
              <div className="text-center">
                <p className="font-serif-sc text-2xl font-bold mb-3">
                  您已是<span className="text-gold-grad">{user.level_name}</span>
                </p>
                <p className="text-sm text-gray-400 mb-6">前往邀请大厅，每邀请1位成员立得 ¥128 奖励</p>
                <Link
                  to="/invite"
                  data-testid="join-goto-invite-btn"
                  className="btn-gold inline-flex items-center gap-2 px-8 py-3 text-sm"
                >
                  进入邀请大厅 <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="text-center">
                <p className="font-serif-sc text-2xl font-bold mb-3">
                  还差一步 · <span className="text-gold-grad">激活会员</span>
                </p>
                <p className="text-sm text-gray-400 mb-6">前往个人中心激活淘宝购买的卡密，开启 ¥128/人 邀请奖励与提现功能</p>
                <Link
                  to="/me"
                  data-testid="join-goto-me-btn"
                  className="btn-gold inline-flex items-center gap-2 px-8 py-3 text-sm"
                >
                  前往激活 <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )
          ) : (
            <div className="text-center">
              <p className="font-mono-num text-[10px] tracking-[0.3em] text-gray-600 mb-2">
                QUICK START
              </p>
              <h3 className="font-serif-sc text-2xl font-bold mb-3">即刻加入 · 开启邀请收益</h3>
              <div className="max-w-xl mx-auto border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-6 py-5 mb-8" data-testid="join-how-block">
                <p className="font-mono-num text-[10px] tracking-[0.3em] text-[#D4AF37] mb-2">加入方式 · HOW TO JOIN</p>
                <p className="text-sm text-gray-300 leading-relaxed">
                  会员资格通过卡密激活。卡密可通过渠道购买，激活后即可获得专属邀请码与邀请奖励资格。
                </p>
              </div>
              <p className="text-sm text-gray-400 mb-8 leading-relaxed">
                激活会员进入项目交流群 · 分享日赚500+稳定项目<br />
                手机号 + 短信验证码，30秒完成注册
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                <button data-testid="join-register-btn" onClick={() => openAuth()} className="btn-gold px-10 py-3.5 text-sm">
                  免费注册
                </button>
                <button
                  data-testid="join-login-btn"
                  onClick={() => openAuth({ mode: "login" })}
                  className="btn-ghost-gold px-10 py-3.5 text-sm"
                >
                  会员登录
                </button>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-xs text-gray-500">
                <span>已是会员？登录后在个人中心激活卡密</span>
                <a
                  data-testid="buy-card-key-link"
                  href={taobao}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[#D4AF37] hover:underline underline-offset-4"
                >
                  <ShoppingBag className="w-3.5 h-3.5" />
                  没有卡密？淘宝搜索「合赢项目交流社区」卡密购买
                </a>
                <Link
                  to="/card-guide"
                  data-testid="join-card-guide-link"
                  className="inline-flex items-center gap-1.5 text-gray-400 hover:text-[#D4AF37] transition-colors"
                >
                  查看图文获取教程
                </Link>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
